/* istanbul ignore file */
import React, {StrictMode, Suspense} from 'react';
import {Helmet, HelmetProvider} from 'react-helmet-async';
import {BrowserRouter, Navigate, useLocation} from 'react-router-dom';

import {DirectionProvider, ToastProvider} from '@veneer/core';

import {GlobalTranslationsProvider, IntlFormatProvider, Loading} from '@br/idm-components';
import {PORTAL_NAME, WolfThemeProvider} from '@br/mg-components';

import '../../layout.scss';

import {SettingsContextProvider} from '../../contexts/SettingsContext';
import {UserContextProvider} from '../../contexts/UserContext';
import '../../i18n';
import {GlobalStyles} from '../GlobalStyles';

type Props = {
    children: React.ReactNode;
};

const Redirects = () => {
    const {pathname} = useLocation();
    return (
        <>
            {pathname === '/' && (
                <Navigate
                    to={'/portal'}
                    replace
                />
            )}
        </>
    );
};

const AppRoot = ({children}: Props): JSX.Element => {
    const rtl = document.documentElement.dir === 'rtl';
    /* Don't use translation to compute this:
        a. it's trivial
        b. it contains an unusual formatting string
        c. this is the first thing to render, so we don't want to have to load translations
     */
    /* eslint-disable i18next/no-literal-string */
    const titleTemplate = rtl ? `%s | ${PORTAL_NAME}` : `${PORTAL_NAME} | %s`;
    /* eslint-enable i18next/no-literal-string */

    const locale = document.documentElement.lang;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        <>
            <StrictMode>
                <WolfThemeProvider
                    mode="light"
                    density="standard"
                    shape="round"
                >
                    <GlobalStyles />
                    <Suspense fallback={<Loading />}>
                        <HelmetProvider>
                            <GlobalTranslationsProvider>
                                <Helmet titleTemplate={titleTemplate} />
                                <DirectionProvider locale={locale}>
                                    <IntlFormatProvider
                                        locale={locale}
                                        timeZone={timeZone}
                                        rtl={rtl}
                                    >
                                        <BrowserRouter>
                                            <Redirects />
                                            <ToastProvider>
                                                <SettingsContextProvider>
                                                    <UserContextProvider>{children}</UserContextProvider>
                                                </SettingsContextProvider>
                                            </ToastProvider>
                                        </BrowserRouter>
                                    </IntlFormatProvider>
                                </DirectionProvider>
                            </GlobalTranslationsProvider>
                        </HelmetProvider>
                    </Suspense>
                </WolfThemeProvider>
            </StrictMode>
        </>
    );
};

export default AppRoot;
